import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AIChatMessagesData,
  AIChatService,
} from 'src/modules/features/admin/services/ai-chat';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  format,
  isToday,
  isThisWeek,
  isThisMonth,
  isThisYear,
  subDays,
} from 'date-fns';
import { history } from '../../ai-chat.data';

@Component({
  selector: 'app-ai-conversation-screen-chats',
  templateUrl: './ai-conversation-screen.component.html',
  styleUrls: ['./ai-conversation-screen.component.scss'],
})
export class AIConversationScreenComponent implements OnInit, AfterViewInit {
  conversation: AIChatMessagesData | undefined;
  sessionId: string | undefined;

  greetingMessage: string | undefined;

  isReply = false;
  pending = false;
  isHistoryPending = false;
  success = false;
  error = false;
  replyForm: FormGroup;
  messages: AIChatMessagesData['rows'] = [];
  history: {
    date_category: string;
    items: {
      session_id: string;
      subject: string;
    }[];
  }[] = [];
  filteredHistory: {
    date_category: string;
    items: {
      session_id: string;
      subject: string;
    }[];
  }[] = [];

  fullScreenMode = false;
  createPending$ = new BehaviorSubject<boolean>(false);
  canReply$ = new Observable<boolean>();

  searchQuery: string = '';

  @ViewChild('scrollContainer', { static: false })
  private chatContainer: ElementRef;

  constructor(
    private fb: FormBuilder,
    private aiChatService: AIChatService,
    private router: Router
  ) {
    this.replyForm = this.fb.group({
      comment: ['', Validators.required],
    });

    this.canReply$ = this.replyForm.statusChanges.pipe(
      map((status) => {
        return status === 'VALID';
      })
    );
  }

  categorizeHistory(
    data: {
      topic: string;
      id: string;
      userId: string;
      sessionMode: string;
      createdAt: string;
      updatedAt: string;
      lastActivityDate: string;
    }[]
  ) {
    const categorizedHistory: {
      date_category: string;
      items: { session_id: string; subject: string }[];
    }[] = [];

    const todayItems = data.filter((item) => isToday(new Date(item.createdAt)));
    if (todayItems.length) {
      categorizedHistory.push({
        date_category: 'today',
        items: todayItems.map((item) => ({
          session_id: item.id,
          subject: item.topic,
        })),
      });
    }

    const last7DaysItems = data.filter(
      (item) =>
        isThisWeek(new Date(item.createdAt)) &&
        !isToday(new Date(item.createdAt))
    );
    if (last7DaysItems.length) {
      categorizedHistory.push({
        date_category: 'previous 7 days',
        items: last7DaysItems.map((item) => ({
          session_id: item.id,
          subject: item.topic,
        })),
      });
    }

    const last30DaysItems = data.filter(
      (item) =>
        isThisMonth(new Date(item.createdAt)) &&
        !isThisWeek(new Date(item.createdAt), { weekStartsOn: 1 })
    );
    if (last30DaysItems.length) {
      categorizedHistory.push({
        date_category: 'previous 30 days',
        items: last30DaysItems.map((item) => ({
          session_id: item.id,
          subject: item.topic,
        })),
      });
    }

    const pastYearItems = data.filter(
      (item) =>
        isThisYear(new Date(item.createdAt)) &&
        !isThisMonth(new Date(item.createdAt))
    );
    if (pastYearItems.length) {
      categorizedHistory.push({
        date_category: format(new Date(), 'yyyy'),
        items: pastYearItems.map((item) => ({
          session_id: item.id,
          subject: item.topic,
        })),
      });
    }

    const previousYearsItems = data.filter(
      (item) => !isThisYear(new Date(item.createdAt))
    );
    const groupedByYear = previousYearsItems.reduce((acc, item) => {
      const year = format(new Date(item.createdAt), 'yyyy');
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(item);
      return acc;
    }, {} as { [key: string]: typeof data });

    for (const year in groupedByYear) {
      categorizedHistory.push({
        date_category: year,
        items: groupedByYear[year].map((item) => ({
          session_id: item.id,
          subject: item.topic,
        })),
      });
    }

    this.history = categorizedHistory;
    this.filteredHistory = categorizedHistory;
  }

  filterChatSessions() {
    if (!this.searchQuery) {
      this.filteredHistory = this.history;
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredHistory = this.history.map((category) => ({
      date_category: category.date_category,
      items: category.items.filter((item) =>
        item.subject.toLowerCase().includes(query)
      ),
    })).filter(category => category.items.length > 0);
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  ngSubmit() {
    let failed = false;
    let isValid = this.replyForm.valid;
    const message = this.replyForm.value.comment;
    this.createPending$.next(true);
    this.replyForm.reset();

    const tempChat = {
      id: this.sessionId,
      request: message,
      requestDate: new Date().toISOString(),
      response: '',
      responseDate: '',
      session: this.sessionId,
      agent: this.sessionId,
      references: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      positiveFeedBack: null,
      positiveFeedBackDate: null,
      negativeFeedBack: null,
      negativeFeedBackDate: null,
    };

    this.messages = [...this.messages, tempChat];
    this.scrollToBottom();

    if (!isValid) {
      return;
    }

    if (!this.sessionId) {
      this.aiChatService.getChatSession().subscribe(
        (data) => {
          this.sessionId = data.session;
          this.router.navigate(['/contractor/ai-chat/screen/ai-user-chat'], {
            queryParams: {
              id: data.session,
            },
          });
          this.aiChatService.getChatSessions().subscribe((data) => {
            this.categorizeHistory(data);
          });
          this.aiChatService
            .sendChatMessage({
              message,
              sessionId: this.sessionId,
            })
            .subscribe(
              (data) => {
                this.createPending$.next(false);
                this.messages[this.messages.length - 1] = {
                  ...data.chat,
                };
                this.scrollToBottom();
              },
              (error) => {
                console.log('ERROR');
                this.messages.pop();
                this.createPending$.next(false);
              }
            );
        },
        (error) => {
          this.messages.pop();
          this.createPending$.next(false);
        }
      );
      return;
    }

    this.aiChatService
      .sendChatMessage({
        message,
        sessionId: this.sessionId,
      })
      .subscribe(
        (data) => {
          this.createPending$.next(false);
          this.messages[this.messages.length - 1] = {
            ...data.chat,
          };
          this.scrollToBottom();
        },
        (error) => {
          this.messages.pop();
          this.createPending$.next(false);
        }
      );
  }

  ngOnInit() {
    this.isHistoryPending = true;
    this.aiChatService.getChatSessions().subscribe(
      (sessions) => {
        this.categorizeHistory(sessions);
        console.log(this.history, 'HISTORY');
        this.isHistoryPending = false;
      },
      (error) => {
        console.log(error, 'ERROR');
      }
    );
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get('id');
    if (sessionId) {
      this.sessionId = sessionId;
      this.getChatMessages(sessionId);
      return;
    }
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
    }
  }

  NewChat() {
    this.messages = [];
    this.sessionId = undefined;
    this.router.navigate(['/contractor/ai-chat/screen/ai-user-chat'], {
      queryParams: {},
    });
  }

  private shouldScrollToBottom = true;

  onScrollDown($event: any) {}

  onScrollUp($event: any) {}

  scrollToBottom(): void {
    console.log('SCROLL TO BOTTOM');
    try {
      setTimeout(() => {
        this.chatContainer.nativeElement.scrollTop =
          this.chatContainer.nativeElement.scrollHeight;
      }, 0);
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
    this.shouldScrollToBottom = false;
  }

  setSessionID(sessionId: string) {
    this.sessionId = sessionId;
    this.messages = [];
    this.router.navigate(['/contractor/ai-chat/screen/ai-user-chat'], {
      queryParams: {
        id: sessionId,
      },
    });
    this.getChatMessages(sessionId);
  }

  getChatMessages(session: string) {
    this.createPending$.next(true);
    this.pending = true;
    this.aiChatService.getChatMessages(session).subscribe(
      (conversation) => {
        this.conversation = conversation;
        this.messages = conversation.rows;
        if (this.messages.length) {
          this.scrollToBottom();
        }
        this.pending = false;
        this.createPending$.next(false);
        this.success = true;
      },
      (error) => {
        this.error = true;
        this.pending = false;
      }
    );
  }

  updateMessage(message: AIChatMessagesData['rows'][0]) {
    this.messages = this.messages.map((msg) => {
      if (msg.id === message.id) {
        return message;
      }
      return msg;
    });
  }
}
